import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Lock, Sparkles } from 'lucide-react'
import { SHELL_MODULES } from '@claritio/frontend/lib/navigation'
import { useWorkspaceOrgId } from '@claritio/frontend/lib/auth'
import { EmptyState } from '@claritio/frontend/components/ui/empty-state'
import { Button } from '@claritio/frontend/components/ui/button'

// Full-page placeholder for sidebar modules not yet built (Clients, Second
// Brain, Reports, Templates, Alerts, Help). titleKey is an admin-namespace key
// (e.g. "nav.secondBrain") so the heading is localized (ENG-111).
export default function ComingSoon({ titleKey }: { titleKey: string }) {
  const { t } = useTranslation('admin')
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center text-center">
      <div className="ai-border mb-6 flex size-14 items-center justify-center rounded-2xl bg-card text-foreground shadow-[var(--shadow-card)]">
        <Sparkles className="size-6" />
      </div>
      <h1>{t(titleKey)}</h1>
      <p className="mt-3 max-w-sm text-sm text-muted-foreground">
        {t('comingSoon.body')}
      </p>
    </div>
  )
}

// /soon/:key — a navigation entry whose screen is not built yet: its name, its
// phase and one sentence on what it will hold.
export function SoonPage() {
  const { key = '' } = useParams()
  const { t, i18n } = useTranslation('admin')
  const item = SHELL_MODULES.flatMap((m) => m.groups.flatMap((g) => g.items)).find((i) => i.key === key)
  const textKey = `shellNav.soonText.${key}`
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center text-center">
      <div className="mb-6 flex size-14 items-center justify-center rounded-2xl bg-card text-foreground shadow-[var(--shadow-card)]">
        <Sparkles className="size-6" />
      </div>
      <div className="mb-2 text-caption text-muted-foreground">{t('shellNav.soonPhase', { phase: item?.phase ?? 1 })}</div>
      <h1>{t(`shellNav.items.${key}`)}</h1>
      <p className="mt-3 max-w-sm text-body text-muted-foreground">
        {i18n.exists(`admin:${textKey}`) ? t(textKey) : t('comingSoon.body')}
      </p>
    </div>
  )
}

// A link to a screen this person may not open: who grants access and a way back to Clara.
export function NoAccessPage() {
  const { t } = useTranslation('admin')
  const orgId = useWorkspaceOrgId()
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <EmptyState
        icon={Lock}
        title={t('shellNav.noAccessPage.title')}
        description={t('shellNav.noAccessPage.body')}
        action={
          <Button asChild variant="outline">
            <Link to={orgId ? `/chat?org_id=${orgId}` : '/home'}>{t(orgId ? 'shellNav.noAccessPage.toClara' : 'shellNav.noAccessPage.toHome')}</Link>
          </Button>
        }
      />
    </div>
  )
}
